import React, { useState, useEffect, FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { InlineIcon } from "@iconify/react";
import {
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
} from "@mui/material";
import * as moment from "moment";
import "moment/locale/fr";

// Styles & assets
import "./Sponsoring.styles.scss";
import { Toastify } from "@/utils/toast";
import ProfileCard from "@/features/profile/Elements/ProfileCard/ProfileCard";
import { routePaths } from "@/config";

// Sélecteurs et types
import {
  selectMemberInfos,
  selectMemberProfile,
} from "@/store/reducers/member/member.selector";
import { TMemberProfile } from "@/features/auth/types";
import {
  TMemberInfos,
  TSubscriptionPlan,
} from "@/features/profile/types/types";

// API
import {
  apiGetSonsoringPlan,
  apiActivateAccountPaypal,
  apiActivateAccountStripe,
  apiCapturePurchasePaypal,
} from "@/features/profile/api/subscription.api";

// Sous-composants
import OfferCard from "./components/OfferCard";
import PaymentDialog from "./components/PaymentDialog";
import { PaypalCardApproveData } from "@/components/PaypalCardFields/PaypalCardFields";
import { calculerAge } from "@/utils/calculerAge";
import { store } from "@/store";
import {
  updateToggleSponsoring,
  updateProductTypePayment,
} from "@/store/reducers/member/member.actions";
import { updateIsPaymentConfirm } from "@/store/reducers/general_param/general_param.actions";
import useUserRestrictions from "@/hooks/useUserRestriction";

// OFFRES DE FALLBACK
const SPONSORING_OFFERS_FALLBACK: TSubscriptionPlan[] = [
  {
    id: "SILVER_ID_MOCK",
    name: "Offre Silver",
    description:
      "Profite de 14 jours de visibilité accrue en étant affiché en priorité sur les recherches et l’accueil.",
    duration: "14 Jours",
    currency: "EUR",
    price_for_men: 3,
    price_for_women: 2,
  },
];

const Sponsoring: FC = () => {
  // ------------------------------
  // 1) States et sélecteurs
  // ------------------------------
  const memberProfile = useSelector(selectMemberProfile) as TMemberProfile;
  const memberInfosFromStore = useSelector(selectMemberInfos) as TMemberInfos;
  const navigate = useNavigate();

  const [offers, setOffers] = useState<TSubscriptionPlan[]>([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState<boolean>(false);

  // Sélection d’une offre + affichage du dialog
  const [selectedOffer, setSelectedOffer] = useState<TSubscriptionPlan | null>(
    null
  );
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] =
    useState<boolean>(false);
  const { isUserSponsored } = useUserRestrictions();

  // ------------------------------
  // 2) Récupération des offres
  // ------------------------------
  useEffect(() => {
    loadSponsoringOffers();
  }, []);

  const translateTimeUnit = (unit: string): string => {
    const normalizedUnit = unit.toUpperCase().trim();

    const translations: Record<string, string> = {
      DAY: "Jour(s)",
      DAYS: "Jour(s)",
      MONTH: "Mois",
      MONTHS: "Mois",
      YEAR: "An(s)",
      YEARS: "An(s)",
      WEEK: "Semaine(s)",
      WEEKS: "Semaine(s)",
    };

    return translations[normalizedUnit] || unit;
  };

  const loadSponsoringOffers = async () => {
    try {
      setIsLoadingOffers(true);
      const response = await apiGetSonsoringPlan();
      const data = response?.data?.result || [];

      const parsedOffers: TSubscriptionPlan[] = data.map((item: any) => ({
        id: item._id,
        name: item.name,
        description: item.description,
        duration: `${item.duration.count} ${translateTimeUnit(item.duration.unit)}`,
        currency: item.currency,
        price_for_men: item.priceForMen,
        price_for_women: item.priceForWomen,
      }));
      setOffers(parsedOffers);
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Toastify(`${error?.response?.data?.message}`, "error", 5000);
      }
      // setOffers(SPONSORING_OFFERS_FALLBACK);
    } finally {
      setIsLoadingOffers(false);
    }
  };

  // ------------------------------
  // 3) Dialog Payment
  // ------------------------------
  const openPaymentDialog = (offerId: string) => {
    const foundOffer = offers.find((o) => o.id === offerId) || null;
    setSelectedOffer(foundOffer);
    setIsPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    setIsPaymentDialogOpen(false);
    setSelectedOffer(null);
  };

  // ------------------------------
  // 4) Paiement
  // ------------------------------
  // Paiement direct via le compte PayPal (wallet) : création de l'ordre côté
  // backend en mode PAYPAL, puis redirection vers le lien d'approbation PayPal.
  const handlePayByPaypalWallet = async () => {
    if (!selectedOffer) return;
    try {
      const payload = {
        categoryPayment: "BOOST_SUBSCRIPTION",
        paymentMean: "PAYPAL",
        paymentPlan: selectedOffer.id,
      };
      const response = await apiActivateAccountPaypal(payload);
      const redirectUrl = response.data?.purchaseUrl;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        Toastify(
          "Impossible d'initialiser le paiement PayPal.",
          "warning",
          5000
        );
        closePaymentDialog();
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Toastify(`${error?.response?.data?.message}`, "error", 5000);
      }
      closePaymentDialog();
    }
  };

  // Paiement via Stripe Checkout (redirect)
  const handlePayByStripe = async () => {
    if (!selectedOffer) return;
    try {
      const payload = {
        categoryPayment: "BOOST_SUBSCRIPTION",
        paymentPlan: selectedOffer.id,
      };
      const response = await apiActivateAccountStripe(payload);
      const redirectUrl = response.data?.purchaseUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        Toastify("Impossible d'initialiser le paiement Stripe.", "warning", 5000);
        closePaymentDialog();
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Toastify(`${error?.response?.data?.message}`, "error", 5000);
      }
      closePaymentDialog();
    }
  };

  // Paiement par carte INTÉGRÉ via PayPal (Card Fields) : création de l'ordre
  // côté backend en mode CARD, l'order id est renvoyé dans subscriptionId.
  const paypalCardCreateOrder = async (): Promise<string> => {
    if (!selectedOffer) throw new Error("Aucune offre sélectionnée.");
    const payload = {
      categoryPayment: "BOOST_SUBSCRIPTION",
      paymentMean: "PAYPAL",
      paymentSourceType: "CARD",
      paymentPlan: selectedOffer.id,
    };
    const response = await apiActivateAccountPaypal(payload);
    const orderId = response.data?.subscriptionId;
    if (!orderId) {
      throw new Error("Impossible d'initialiser le paiement par carte.");
    }
    return orderId;
  };

  // Après saisie de la carte + 3DS : capture, puis bascule vers la page de
  // confirmation (polling) via le store, comme le flux redirect.
  const paypalCardOnApprove = async (data: PaypalCardApproveData) => {
    const response: any = await apiCapturePurchasePaypal(data.orderID);
    if (!response?.data?.success) {
      throw new Error(
        response?.data?.requiresRetry
          ? "L'authentification de votre carte n'a pas abouti. Veuillez réessayer ou utiliser une autre carte."
          : "Le paiement n'a pas pu être finalisé. Veuillez réessayer."
      );
    }
    closePaymentDialog();
    store.dispatch(updateProductTypePayment("boost"));
    store.dispatch(updateIsPaymentConfirm(true));
  };

  // ------------------------------
  // 5) Prix selon le genre
  // ------------------------------
  const getPriceByGender = (offer: TSubscriptionPlan) => {
    const gender = memberProfile?.infos?.identity?.gender;
    if (!gender) return offer.price_for_men;
    return gender === "MALE" ? offer.price_for_men : offer.price_for_women;
  };

  // ------------------------------
  // 6) Navigation
  // ------------------------------
  const goTo = (path: string) => {
    navigate(path, { replace: true });
  };

  // 👇 État local pour le test rapide (à retirer facilement après l'intégration API)
  const [hasActiveSponsoring, setHasActiveSponsoring] = useState(
    memberProfile.isSponsored
  );

  // Exemple données souscription active à récupérer depuis API/backend
  const activeSponsoringDetails = {
    subscriptionDate: "2025-03-15",
    validityPeriod: "14 Jours",
    amountPaid: "3 €",
    remainingDays: 7,
  };

  // ------------------------------
  // 7) Rendu JSX
  // ------------------------------
  return (
    <div className="ct-sponsor">
      {/* NAV PART */}
      <div className="ct-sponsor__nav-part">
        <Button
          color="primary"
          className="ct-sponsor__nav-part__btn-nav"
          onClick={() =>
            goTo(routePaths.profileChildren.myAccountChildren.ficheAbsolute)
          }
          startIcon={<InlineIcon icon="ant-design:file-search-outlined" />}
        >
          Accédez à votre fiche
        </Button>

        <Button
          color="primary"
          className="ct-sponsor__nav-part__btn-nav"
          onClick={() =>
            goTo(routePaths.profileChildren.myAccountChildren.formAbsolute)
          }
          startIcon={<InlineIcon icon="material-symbols:lab-profile-outline" />}
        >
          Accédez à votre profil
        </Button>
      </div>

      {/* BODY PART */}
      <div className="ct-sponsor__body-part">
        <div className="ct-sponsor__body-part__card-part">
          {/* Section d’appel */}
          {!isUserSponsored && (
            <div className="explanatory-section">
              <Typography variant="h4" className="title" gutterBottom>
                ✨ Devenez plus visible !
              </Typography>
              <Typography variant="h6" className="subtitle" paragraph>
                Boostez dès maintenant votre profil grâce à notre toute première
                offre de <b>sponsoring</b>, et soyez rapidement mis en avant
                auprès des autres membres.
              </Typography>
            </div>
          )}

          {/* SECTION BÉNÉFICES + CARTE PRÉVISUALISATION */}
          {isUserSponsored ? (
            // État actif de la souscription
            <div className="subscription-active-section">
              <Typography
                variant="h5"
                className="subscription-active-title"
                gutterBottom
              >
                🚀 Votre profil est actuellement boosté !
              </Typography>

              <div className="subscription-details">
                <Typography>
                  <b>Date de souscription :</b>{" "}
                  {moment
                    .default(
                      memberProfile?.account?.subscription?.lastBoost?.startDate
                    )
                    .format("LL") +
                    " " +
                    moment
                      .default(
                        memberProfile?.account?.subscription?.lastBoost
                          ?.startDate
                      )
                      .format("LT")}
                </Typography>
                <Typography>
                  <b>Période de validité :</b>{" "}
                  {Math.abs(
                    moment
                      .default(
                        memberProfile?.account?.subscription?.lastBoost
                          ?.startDate
                      )
                      .diff(
                        moment.default(
                          memberProfile?.account?.subscription?.lastBoost
                            ?.endDate
                        ),
                        "days"
                      )
                  )}{" "}
                  jours
                </Typography>
                <Typography>
                  <b>Montant réglé :</b>{" "}
                  {memberProfile?.account?.subscription?.lastBoost?.amount} €
                </Typography>
                <Typography color="primary">
                  <b>Jours restants :</b>{" "}
                  {Math.abs(
                    moment
                      .default(
                        memberProfile?.account?.subscription?.lastBoost?.endDate
                      )
                      .diff(moment.default(new Date()), "days")
                  )}{" "}
                  jours
                </Typography>
              </div>

              {false && (
                <Button
                  variant="contained"
                  className="renew-subscription-btn"
                  onClick={() => openPaymentDialog(offers[0]?.id)}
                  endIcon={<InlineIcon icon="ic:twotone-rocket-launch" />}
                >
                  Prolonger mon boost
                </Button>
              )}
            </div>
          ) : (
            // Affichage avantages initial
            <div className="benefits-preview-section">
              <div className="benefits-preview-section__benefits">
                <Typography
                  variant="h5"
                  className="benefits-title"
                  gutterBottom
                >
                  Les avantages d’un profil sponsorisé
                </Typography>
                <List className="benefits-list">
                  <ListItem>
                    <ListItemIcon>
                      <InlineIcon icon="material-symbols:star-outline" />
                    </ListItemIcon>
                    <ListItemText primary="Apparaissez sur la page d'accueil en priorité" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InlineIcon icon="mdi:shield-star-outline" />
                    </ListItemIcon>
                    <ListItemText primary="Recevez un badge exclusif attractif" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InlineIcon icon="ic:round-search" />
                    </ListItemIcon>
                    <ListItemText primary="Boostez votre visibilité dans les résultats" />
                  </ListItem>
                </List>
              </div>

              <div className="benefits-preview-section__preview-card">
                <Typography
                  variant="body1"
                  className="preview-card-title"
                  gutterBottom
                >
                  Voici à quoi ressemblera votre profil
                </Typography>
                <InlineIcon icon="icon-park-twotone:down-two" />
                <div className="preview-card-container">
                  <ProfileCard
                    isDemo={true}
                    username={
                      memberProfile?.infos?.identity?.username || "Utilisateur"
                    }
                    profil_id={memberInfosFromStore?.memberId || "ID_INCONNU"}
                    age={
                      calculerAge(
                        new Date(memberProfile?.infos?.identity?.birthDate)
                      ) || 0
                    }
                    town={
                      memberProfile?.infos?.identity?.city || "Ville inconnue"
                    }
                    country={
                      memberProfile?.infos?.identity?.country || "Pays inconnu"
                    }
                    origine={
                      memberProfile?.infos?.profile?.homeCountry ||
                      "Origine inconnue"
                    }
                    otherHomeCountry={
                      memberProfile?.infos?.profile?.otherHomeCountry ||
                      "Autre pays d'origine inconnu"
                    }
                    nationalite={
                      memberProfile?.infos?.profile?.citizenship ||
                      "Nationalité inconnue"
                    }
                    description={
                      memberProfile?.infos?.profile?.description ||
                      "Aucune description disponible"
                    }
                    progressValue={
                      Math.round(memberProfile?.rateOfCompletion) || 0
                    }
                    isVerified={true}
                    isOnline={true}
                    isSponsored={true}
                    hasSubscription={true}
                    currentConversation={null}
                    gender={memberProfile?.infos?.identity?.gender || "UNKNOWN"}
                  />
                </div>
              </div>
            </div>
          )}

          {/* OFFRES DE SPONSORING */}
          {!isUserSponsored && (
            <Grid
              container
              spacing={3}
              className={`offers-container ${
                offers.length === 1 ? "single-offer" : ""
              }`}
            >
              {isLoadingOffers ? (
                <Grid item xs={12} className="loading-container">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <CircularProgress color="primary" />
                    <Typography variant="body2" mt={2}>
                      Chargement des offres...
                    </Typography>
                  </Box>
                </Grid>
              ) : offers.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body1">
                    Aucune offre de sponsoring disponible.
                  </Typography>
                </Grid>
              ) : (
                offers.map((offer) => {
                  const price = getPriceByGender(offer);
                  return (
                    <Grid
                      item
                      xs={12}
                      key={offer.id}
                      className={offers.length === 1 ? "centered-card" : ""}
                    >
                      <OfferCard
                        offer={offer}
                        price={price}
                        onSubscribe={() => openPaymentDialog(offer.id)}
                      />
                    </Grid>
                  );
                })
              )}
            </Grid>
          )}
        </div>
      </div>

      {/* Dialog de paiement */}
      <PaymentDialog
        open={isPaymentDialogOpen}
        offer={selectedOffer}
        onClose={closePaymentDialog}
        onPayByPaypalWallet={handlePayByPaypalWallet}
        onPayByStripe={handlePayByStripe}
        paypalCardCreateOrder={paypalCardCreateOrder}
        paypalCardOnApprove={paypalCardOnApprove}
        getPriceByGender={getPriceByGender}
      />
    </div>
  );
};

export default Sponsoring;
